// Board document model and sync op types (spec §4.2, §6.2). Shared by client and server.

export type ObjectType = 'sticky' | 'shape' | 'text' | 'connector' | 'image' | 'audio' | 'freehand' | 'frame' | 'card';

export interface RichTextRun { t: string; b?: 1; i?: 1; href?: string }
export interface RichTextBlock { list?: 'bullet'; runs: RichTextRun[] }
/** Minimal rich text: paragraphs of runs. Only bold, italic, link, bullet list. */
export interface RichText { blocks: RichTextBlock[] }

export interface BaseObject {
  id: string;          // client-generated, 96-bit random, base62
  type: ObjectType;
  x: number; y: number; w: number; h: number;
  rotation: number;    // radians, about the object's centre
  z: string;           // fractional index
  locked?: boolean;
  createdBy: string;
}

export type ShapeKind = 'rect' | 'ellipse' | 'diamond' | 'triangle';

export interface Sticky extends BaseObject { type: 'sticky'; color: string; text: RichText }
export interface Shape extends BaseObject {
  type: 'shape'; kind: ShapeKind;
  fill: string; stroke: string; strokeWidth: number; text?: RichText;
}
export interface TextObj extends BaseObject { type: 'text'; text: RichText; fontSize: number }
export interface Image extends BaseObject { type: 'image'; assetHash: string }
/** An audio clip (spec §19): played in place; the waveform is stored, so drawing needs no download. */
export interface Audio extends BaseObject {
  type: 'audio';
  assetHash: string;
  mime: string;        // one of AUDIO_TYPES
  duration: number;    // seconds
  peaks: number[];     // up to AUDIO.peaks levels, 0..1
  title: string;
}
export interface Freehand extends BaseObject {
  type: 'freehand';
  points: number[]; // flat [x,y,...] relative to x,y
  stroke: string; strokeWidth: number;
}
export interface Frame extends BaseObject {
  type: 'frame'; title: string; children: string[];
  /** 'column': stacks its cards like a Kanban column (spec §13.6). */
  layout?: 'column';
}
export interface Connector extends BaseObject {
  type: 'connector';
  from: Endpoint; to: Endpoint; routing: 'straight' | 'elbow';
  stroke: string; arrowStart: boolean; arrowEnd: boolean;
}

/** A light task card (spec §13): title, optional note, checklist, who's involved, due date. */
export interface Card extends BaseObject {
  type: 'card';
  title: string;                          // plain text
  note?: RichText;
  color: string;                          // label strip
  items: Record<string, ChecklistItem>;   // keyed by item id, merged per item (spec §13.2)
  assignees: string[];                    // user ids, display only
  due?: string;                           // 'YYYY-MM-DD'
}

export interface ChecklistItem {
  text: string;
  done: boolean;
  order: string;       // fractional index within the card
  assignee?: string;   // user id, display only
}

export type Endpoint =
  | { objectId: string; anchor: [number, number] } // anchor in 0..1 of target bounds
  | { point: [number, number] };                   // free-floating end

export type BoardObject = Sticky | Shape | TextObj | Image | Audio | Freehand | Frame | Connector | Card;

export type Op =
  | { t: 'create'; obj: BoardObject }
  | { t: 'update'; id: string; props: Partial<BoardObject> }
  | { t: 'delete'; id: string }
  // One checklist item of a card: props merge into it (creating it if absent); null removes it.
  | { t: 'item'; id: string; itemId: string; props: Partial<ChecklistItem> | null };

export type ItemOp = Extract<Op, { t: 'item' }>;

export interface ClientOpBatch { type: 'ops'; ops: { clientOpId: string; op: Op }[] }
export interface ServerOp { seq: number; userId: string; clientOpId: string; op: Op }

export const emptyRichText = (): RichText => ({ blocks: [] });
